import {faUpload} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {LangKeyType} from '../../provider/locale/translation/type';
import {CustomFileType, UploadFileResponseType} from '../../service/file-upload/file-upload-type';
import {useFormRules} from '../../service/form/form-rules-hook';
import {classNames} from '../../util/css';
import {FileFormInput} from '../form/file-upload-item/file-form-input';
import {Text} from '../text/text';

import * as styles from './publication-media-uploader.scss';

type PublicationMediaImageType = {id: number; url: string};

// Default upload size cap (MB); each publication type may override via props.
const PUBLICATION_MEDIA_DEFAULT_MAX_FILE_SIZE = 10;

type FilesMapType = Record<string, CustomFileType & UploadFileResponseType>;

type CommonPropsType = {
    value?: Array<PublicationMediaImageType>;
    onChange?: (images: Array<PublicationMediaImageType>) => void;
    // Reports uploads that are still in flight or failed, so the caller can block submitting.
    onBlockedChange?: (isBlocked: boolean) => void;
    upload: (file: RcFile) => Promise<{id: number}>;
    remove?: (imageId: number) => void;
    maxCount: number;
    // Allowed image dimensions (px). Differ per publication type per Yandex help.
    minWidth: number;
    minHeight: number;
    maxWidth: number;
    maxHeight: number;
    maxFileSize?: number;
    uploaderTextLangKey: LangKeyType;
};

// `tiles` — square thumbnails with an upload tile, for fields holding several images (events). Its
// empty state is the upload rules banner, so that layout cannot do without one.
// `list` — a full-width upload button with the uploaded files as rows below it (promotions). Those
// fields keep the rules in the field's tooltip, so there is nowhere to render a banner.
type PropsType = CommonPropsType & ({layout: 'tiles'; banner: ReactNode} | {layout: 'list'; banner?: never});

function buildInitialFilesMap(images: Array<PublicationMediaImageType>): FilesMapType {
    return Object.fromEntries(
        images.map((image) => [
            String(image.id),
            {
                uid: String(image.id),
                id: String(image.id),
                name: `image-${image.id}`,
                url: image.url,
                status: 'done' as const,
            },
        ])
    );
}

// Compact image uploader for the single-step publication forms, used as a Form.Item control.
export function PublicationMediaUploader(props: PropsType): JSX.Element {
    const {
        value,
        onChange,
        onBlockedChange,
        upload,
        remove,
        maxCount,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        maxFileSize = PUBLICATION_MEDIA_DEFAULT_MAX_FILE_SIZE,
        layout,
        banner,
        uploaderTextLangKey,
    } = props;

    const {validateImageDimension} = useFormRules();

    const [filesMap, setFilesMap] = useState<FilesMapType>(() => buildInitialFilesMap(value ?? []));

    // The initial map is built from the form value, so reporting it back would only mark the field as
    // changed — and make a required rule fail on a form the user has not touched yet.
    const isInitialRunRef = useRef<boolean>(true);

    useEffect(() => {
        const entries = Object.values(filesMap);

        onBlockedChange?.(entries.some((file) => file.status === 'uploading' || file.status === 'error'));

        if (isInitialRunRef.current) {
            isInitialRunRef.current = false;

            return;
        }

        if (entries.some((file) => file.status === 'uploading')) {
            return;
        }

        onChange?.(
            entries.filter((file) => file.status === 'done').map((file) => ({id: Number(file.id), url: file.url}))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesMap]);

    const uploadWrapper = useCallback(
        async (file: RcFile): Promise<UploadFileResponseType> => {
            const {id} = await upload(file);

            return {id: String(id), url: URL.createObjectURL(file), name: file.name};
        },
        [upload]
    );

    const handleRemove = useCallback(
        (fileUid: string) => {
            const imageId = Number(fileUid);

            if (!Number.isNaN(imageId)) {
                remove?.(imageId);
            }
        },
        [remove]
    );

    const fileList = Object.values(filesMap);
    const isEmpty = fileList.length === 0;
    const isListLayout = layout === 'list';

    function renderTrigger(): ReactNode {
        if (isListLayout) {
            return (
                <Button className={styles.PublicationMediaUploader_uploadButton}>
                    <FontAwesomeIcon icon={faUpload} />
                    <Text stringKey={uploaderTextLangKey} />
                </Button>
            );
        }

        if (isEmpty) {
            return <div className={styles.PublicationMediaUploader_banner}>{banner}</div>;
        }

        return (
            <div className={styles.PublicationMediaUploader_tile}>
                <FontAwesomeIcon className={styles.PublicationMediaUploader_tileIcon} icon={faUpload} />
                <Text small stringKey={uploaderTextLangKey} />
            </div>
        );
    }

    return (
        <div
            className={classNames(styles.PublicationMediaUploader, {
                [styles.PublicationMediaUploader__fullWidthTrigger]: isListLayout || isEmpty,
            })}
        >
            <FileFormInput
                onChange={setFilesMap}
                remove={handleRemove}
                upload={uploadWrapper}
                // Only reaches the DOM in the `list` layout: the `tiles` layout keeps Ant's own list.
                uploadListClassName={styles.PublicationMediaUploader_list}
                uploadProps={{
                    accept: '.png,.jpg',
                    // Thumbnail tiles once there is something to show; the empty state is the banner,
                    // which must not be squeezed into a picture-card tile.
                    listType: isListLayout || isEmpty ? 'picture' : 'picture-card',
                    maxCount,
                    maxFileSize,
                    showUploadList: isListLayout ? false : {showPreviewIcon: false},
                }}
                validationRules={[
                    async (file: RcFile) =>
                        validateImageDimension(file, {
                            min: {height: minHeight, width: minWidth},
                            max: {height: maxHeight, width: maxWidth},
                        }),
                ]}
                value={filesMap}
            >
                {renderTrigger()}
            </FileFormInput>
        </div>
    );
}
